import type { UTMParams } from './utm'

let initializedMeasurementId: string | null = null

const GA_SCRIPT_SELECTOR = 'script[data-ga4-script="true"]'

/**
 * GA4 配置
 */
export interface AnalyticsConfig {
  measurementId: string
  enabled?: boolean
  debug?: boolean
  sendPageView?: boolean
}

/**
 * GA4 事件参数
 */
export interface EventParams {
  event_name: string
  custom_params?: Record<string, string | number | boolean>
  items?: any[]
}

/**
 * 初始化 GA4
 */
export function initAnalytics(config: AnalyticsConfig): void {
  if (!config.enabled && typeof config.enabled !== 'undefined') {
    console.log('Google Analytics 已禁用')
    return
  }

  if (!config.measurementId) {
    console.warn('Google Analytics measurementId 未配置，已跳过初始化')
    return
  }

  try {
    const existingScript = document.querySelector<HTMLScriptElement>(
      `${GA_SCRIPT_SELECTOR}[data-ga4-measurement-id="${config.measurementId}"]`,
    )

    if (!existingScript) {
      // 加载 gtag.js
      const script = document.createElement('script')
      script.async = true
      script.src = `https://www.googletagmanager.com/gtag/js?id=${config.measurementId}`
      script.dataset.ga4Script = 'true'
      script.dataset.ga4MeasurementId = config.measurementId
      document.head.appendChild(script)
    }

    // 初始化 gtag
    window.dataLayer = window.dataLayer || []
    const shouldInitGtag = !window.gtag

    if (!window.gtag) {
      window.gtag = function gtag() {
        window.dataLayer?.push(arguments)
      }
    }

    if (shouldInitGtag) {
      window.gtag('js', new Date())
    }

    if (initializedMeasurementId === config.measurementId) {
      return
    }

    window.gtag('config', config.measurementId, {
      debug_mode: config.debug || false,
      send_page_view: config.sendPageView ?? false,
    })
    initializedMeasurementId = config.measurementId

    console.log(`Google Analytics 已初始化: ${config.measurementId}`)
  } catch (error) {
    console.error('初始化 Google Analytics 失败:', error)
  }
}

/**
 * 发送页面浏览事件
 */
export function trackPageView(
  pagePath?: string,
  pageTitle?: string,
  utmParams?: UTMParams,
): void {
  if (!window.gtag) return

  try {
    const params: any = {
      page_path: pagePath || window.location.pathname,
      page_title: pageTitle || document.title,
    }

    // 添加 UTM 参数
    if (utmParams) {
      Object.entries(utmParams).forEach(([key, value]) => {
        if (value) {
          params[key] = value
        }
      })
    }

    window.gtag('event', 'page_view', params)
  } catch (error) {
    console.error('追踪页面浏览失败:', error)
  }
}

/**
 * 发送自定义事件
 */
export function trackEvent(
  eventName: string,
  customParams?: Record<string, string | number | boolean>,
  utmParams?: UTMParams,
): void {
  if (!window.gtag) return

  try {
    const params: any = {
      ...customParams,
    }

    // 添加 UTM 参数
    if (utmParams) {
      Object.entries(utmParams).forEach(([key, value]) => {
        if (value) {
          params[key] = value
        }
      })
    }

    window.gtag('event', eventName, params)
  } catch (error) {
    console.error('追踪事件失败:', error)
  }
}

/**
 * 追踪用户登录
 */
export function trackLogin(method: string, utmParams?: UTMParams): void {
  trackEvent('login', { method }, utmParams)
}

/**
 * 追踪用户注册
 */
export function trackSignUp(method: string, utmParams?: UTMParams): void {
  trackEvent('sign_up', { method }, utmParams)
}

// /**
//  * 追踪交易
//  */
// export function trackTransaction(
//   transactionId: string,
//   value: number,
//   currency: string,
//   items: any[],
//   utmParams?: UTMParams,
// ): void {
//   trackEvent(
//     'purchase',
//     {
//       transaction_id: transactionId,
//       value,
//       currency,
//       items,
//     },
//     utmParams,
//   )
// }

/**
 * 追踪分享
 */
export function trackShare(
  contentType: string,
  itemId: string,
  method: string,
  utmParams?: UTMParams,
): void {
  trackEvent(
    'share',
    { content_type: contentType, item_id: itemId, method },
    utmParams,
  )
}

/**
 * 追踪搜索
 */
export function trackSearch(searchTerm: string, utmParams?: UTMParams): void {
  trackEvent('search', { search_term: searchTerm }, utmParams)
}

/**
 * 追踪选择内容
 */
export function trackSelectContent(
  contentType: string,
  itemId: string,
  utmParams?: UTMParams,
): void {
  trackEvent(
    'select_content',
    { content_type: contentType, item_id: itemId },
    utmParams,
  )
}

/**
 * 设置用户 ID
 */
export function setUserId(userId: string): void {
  if (!window.gtag) return
  if (!initializedMeasurementId) return

  try {
    window.gtag('config', initializedMeasurementId, {
      user_id: userId,
    })
  } catch (error) {
    console.error('设置用户 ID 失败:', error)
  }
}

/**
 * 设置用户属性
 */
export function setUserProperties(
  properties: Record<string, string | number | boolean>,
): void {
  if (!window.gtag) return

  try {
    window.gtag('set', 'user_properties', properties)
  } catch (error) {
    console.error('设置用户属性失败:', error)
  }
}

/**
 * 获取 gtag 实例
 */
export function getGtag() {
  return window.gtag
}

// 类型声明
declare global {
  interface Window {
    dataLayer?: any[]
    gtag?: (...args: any[]) => void
  }
}
